import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users as UsersIcon, Plus, Trash2, Mail, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { projectMembersApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function ProjectMembersPanel({ projectId, myRole }) {
  const { user: me } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await projectMembersApi.list(projectId);
      setMembers(data);
    } catch {
      // Editors/Viewers also see this (allowed for viewers).
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) load();
     
  }, [projectId]);

  // Project-admins (or global admins, who get my_role="admin" on every project) can manage members.
  const isAdmin = myRole === "admin" || me?.role === "admin";

  const handleRemove = async (userId, email) => {
    try {
      await projectMembersApi.remove(projectId, userId);
      toast.success(`${email} removed`);
      load();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await projectMembersApi.updateRole(projectId, userId, newRole);
      toast.success("Role updated");
      load();
    } catch {
      toast.error("Failed to update role");
    }
  };

  return (
    <section
      data-testid="project-members-panel"
      className="rounded-md border border-zinc-800 bg-zinc-900/60 p-6"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <UsersIcon className="w-4 h-4 text-amber-500" />
          <div className="text-overline text-zinc-400">Project Members</div>
          <span className="text-xs text-zinc-500 font-mono">· {members.length}</span>
        </div>
        {isAdmin && (
          <Button
            data-testid="invite-member-btn"
            onClick={() => setOpen(true)}
            className="bg-amber-500 text-black hover:bg-amber-400 h-8 text-xs font-semibold"
          >
            <Plus className="w-3 h-3 mr-1" /> Invite
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
        </div>
      ) : members.length === 0 ? (
        <div className="text-sm text-zinc-500 italic">
          {isAdmin
            ? "No members yet. Invite editors or viewers to collaborate."
            : "Only admins can manage members."}
        </div>
      ) : (
        <div className="space-y-2">
          {members.map((m) => (
            <div
              key={m.user_id}
              data-testid={`member-row-${m.user_id}`}
              className="flex items-center gap-3 p-2 rounded-sm bg-zinc-950 border border-zinc-800"
            >
              <div className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-xs font-semibold text-zinc-400 shrink-0">
                {(m.name || m.email || "").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 truncate">{m.name || m.email}</div>
                <div className="text-xs text-zinc-500 truncate font-mono">{m.email}</div>
              </div>
              {isAdmin ? (
                <>
                  <Select
                    value={m.project_role}
                    onValueChange={(v) => handleRoleChange(m.user_id, v)}
                  >
                    <SelectTrigger
                      data-testid={`member-role-select-${m.user_id}`}
                      className="bg-zinc-900 border-zinc-800 text-xs h-7 w-24"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="contributor">Contributor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <button
                    data-testid={`remove-member-${m.user_id}`}
                    onClick={() => handleRemove(m.user_id, m.email)}
                    className="p-1.5 rounded-sm text-zinc-500 hover:text-rose-400"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <span className="text-xs text-zinc-500 uppercase tracking-wider">
                  {m.project_role}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {isAdmin && (
        <InviteDialog
          open={open}
          onOpenChange={setOpen}
          projectId={projectId}
          onSent={load}
        />
      )}
    </section>
  );
}

function InviteDialog({ open, onOpenChange, projectId, onSent }) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null); // existing user picked from list
  const [role, setRole] = useState("editor");
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setPicked(null);
    setRole("editor");
    setShowSuggestions(false);
    setUsersLoaded(false);
    (async () => {
      try {
        const data = await projectMembersApi.invitableUsers(projectId);
        setUsers(data || []);
      } catch {
        // If load fails (e.g. project-admin without global-admin), fall back silently to email-only
        setUsers([]);
      } finally {
        setUsersLoaded(true);
      }
    })();
  }, [open, projectId]);

  // Filter suggestions by name OR email substring (case-insensitive). Hide if a user is picked.
  const suggestions = useMemo(() => {
    if (picked) return [];
    const q = query.trim().toLowerCase();
    if (!q) return users.slice(0, 8);
    return users
      .filter(
        (u) =>
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [users, query, picked]);

  // If the typed query exactly matches an existing user's email, treat as that user
  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return users.find((u) => (u.email || "").toLowerCase() === q) || null;
  }, [users, query]);

  const effectivePick = picked || exactMatch;
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(query.trim());
  const canSubmit = !!effectivePick || isValidEmail;

  const handlePick = (user) => {
    setPicked(user);
    setQuery(user.email);
    setShowSuggestions(false);
  };

  const handleClearPick = () => {
    setPicked(null);
    setQuery("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const email = (effectivePick?.email || query).trim().toLowerCase();
      const res = await projectMembersApi.invite(projectId, { email, role });
      toast.success(
        res.added_existing
          ? `${effectivePick?.name || email} added to project`
          : `Invitation sent to ${email}`
      );
      onSent && onSent();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to invite");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Invite to project
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Pick an existing user from the list, or type any email to send a new invitation.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3" autoComplete="off">
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">User</Label>
            {picked ? (
              <div
                data-testid="picked-user-chip"
                className="flex items-center gap-3 p-2 rounded-sm bg-amber-500/10 border border-amber-500/30"
              >
                <div className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-xs font-semibold text-zinc-300 shrink-0">
                  {(picked.name || picked.email || "").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-100 truncate">{picked.name || picked.email}</div>
                  <div className="text-xs text-zinc-500 truncate font-mono">{picked.email}</div>
                </div>
                <button
                  type="button"
                  onClick={handleClearPick}
                  data-testid="clear-picked-user"
                  className="p-1 rounded-sm text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900"
                  title="Change"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Mail className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  data-testid="invite-email-input"
                  type="text"
                  inputMode="email"
                  name="invite-user-query"
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder="Search registered users or enter email…"
                  required
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 pl-9"
                />
                {showSuggestions && usersLoaded && suggestions.length > 0 && (
                  <div
                    data-testid="user-suggestions"
                    className="absolute z-50 left-0 right-0 mt-1 bg-zinc-950 border border-zinc-800 rounded-sm shadow-2xl max-h-64 overflow-auto"
                  >
                    {suggestions.map((u) => (
                      <button
                        type="button"
                        key={u.id}
                        data-testid={`user-suggestion-${u.id}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handlePick(u);
                        }}
                        className="w-full flex items-center gap-3 p-2 text-left hover:bg-zinc-900 transition-colors"
                      >
                        <div className="w-7 h-7 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[10px] font-semibold text-zinc-400 shrink-0">
                          {(u.name || u.email || "").slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-zinc-100 truncate">{u.name || u.email}</div>
                          <div className="text-[11px] text-zinc-500 truncate font-mono">{u.email}</div>
                        </div>
                      </button>
                    ))}
                    {query.trim() && isValidEmail && !exactMatch && (
                      <div className="p-2 border-t border-zinc-800 text-xs text-zinc-500 italic">
                        Press <strong className="text-zinc-300">Send</strong> to invite{" "}
                        <span className="text-amber-500 font-mono">{query}</span> by email.
                      </div>
                    )}
                  </div>
                )}
                {showSuggestions && usersLoaded && users.length === 0 && !query && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-zinc-950 border border-zinc-800 rounded-sm shadow-2xl p-3 text-xs text-zinc-500">
                    No other registered users yet — type an email to send the first invitation.
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Project role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="invite-role-select" className="bg-zinc-950 border-zinc-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="editor">Editor — can edit shot list, upload & download files</SelectItem>
                <SelectItem value="contributor">Contributor — upload/download files & upload videos; no plan edits</SelectItem>
                <SelectItem value="viewer">Viewer — read-only; can preview files but not download</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !canSubmit}
              data-testid="invite-submit-btn"
              className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm font-semibold"
            >
              {submitting
                ? "Inviting…"
                : effectivePick
                  ? "Add to project"
                  : "Send invitation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
